// My Hours — employee time tracking. Every Command user logs their own
// total hours per day (one entry per day; re-saving a day overwrites it).
// Admins additionally see the All Employees manager view with a date
// range and a CSV export for payroll.
//
// Expenses can carry an optional receipt (PDF or image). The receipt is
// uploaded base64-encoded and streamed back through the authenticated
// API (see openReceipt) — never via a URL token.
//
// Mobile: on a narrow screen the page becomes two tabs (Hours /
// Expenses), the recent-entry tables become tap-friendly cards, and the
// receipt button opens the phone camera. The payroll/all-employees
// manager views stay desktop-only. Desktop layout is unchanged.
//
// Dave 2026-10-15.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';

// Local dates (toISOString is UTC, which turns into "tomorrow" after 8 pm in Toronto).
function todayISO() {
  return dateToISO(new Date());
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return dateToISO(d);
}
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Hour types: regular (worked), holiday (paid stat holiday), vacation
// (paid time off). Vacation added Dave 2026-10-27.
const HOUR_TYPES = [
  { key: 'regular', label: 'Regular', option: 'Regular' },
  { key: 'holiday', label: 'Holiday', option: 'Holiday (paid)' },
  { key: 'vacation', label: 'Vacation', option: 'Vacation' },
];
function typeLabel(t) {
  return (HOUR_TYPES.find((x) => x.key === t) || HOUR_TYPES[0]).label;
}
// { regular, holiday, vacation, total } hours for a list of entries.
function hourBreakdown(entries) {
  const out = { regular: 0, holiday: 0, vacation: 0, total: 0 };
  for (const e of entries) {
    const k = HOUR_TYPES.some((x) => x.key === e.entry_type) ? e.entry_type : 'regular';
    out[k] += Number(e.hours) || 0;
  }
  for (const k of Object.keys(out)) if (k !== 'total') out[k] = round2(out[k]);
  out.total = round2(out.regular + out.holiday + out.vacation);
  return out;
}
// "Regular" when it's all worked hours, else "32 regular · 8 vacation".
function breakdownText(b) {
  if (!b.holiday && !b.vacation) return 'Regular';
  return HOUR_TYPES.filter((x) => b[x.key]).map((x) => `${b[x.key]} ${x.label.toLowerCase()}`).join(' · ');
}
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EXPENSE_CATEGORIES = ['Meals', 'Fuel', 'Supplies', 'Travel', 'Parking', 'Lodging', 'Other'];

// Receipt upload limits (kept in sync with the server).
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const RECEIPT_ACCEPT = 'image/*,application/pdf,.pdf,.heic,.heif';

// True on phone-width screens. Drives the tabbed / card layout.
function useIsMobile(query = '(max-width: 720px)') {
  const get = () => (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia(query).matches : false;
  const [m, setM] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const handler = () => setM(mq.matches);
    handler();
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [query]);
  return m;
}

// Styles for the mobile tabs + cards. Scoped by the tt- prefix and
// injected once so we don't have to touch the global stylesheet.
const MOBILE_CSS = `
.tt-tabs{display:flex;gap:6px;background:var(--color-surface-2,#f1f5f9);padding:6px;border-radius:12px;margin:0 0 16px}
.tt-tab{flex:1;text-align:center;padding:11px 0;font-size:14px;font-weight:600;color:var(--color-text-dim,#64748b);border-radius:9px;border:none;background:transparent;font-family:inherit;cursor:pointer}
.tt-tab.is-active{background:var(--color-surface,#fff);color:var(--color-accent,#0d9488);box-shadow:0 1px 3px rgba(15,23,42,.12)}
.tt-cards{display:flex;flex-direction:column;gap:9px;list-style:none;padding:0;margin:0}
.tt-card{display:flex;align-items:center;gap:12px;background:var(--color-surface,#fff);border:1px solid var(--color-border,#e5e7eb);border-radius:12px;padding:12px 14px}
.tt-card-title{font-size:16px;font-weight:700}
.tt-card-sub{font-size:12.5px;color:var(--color-text-dim,#64748b);margin-top:3px;line-height:1.4}
.tt-card-actions{margin-left:auto;display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.tt-empty{color:var(--color-text-dim,#64748b);padding:14px 2px;font-size:14px}
@media (max-width:720px){
  .tt-form .form-field{flex:1 1 100%}
  .tt-form input:not([type=checkbox]),.tt-form select{min-height:46px;font-size:16px}
  .tt-form>.btn-primary,.tt-form>.btn-secondary{min-height:46px;flex:1 1 100%}
}
`;

// Fetch a receipt through the authenticated API and open it in a new
// tab. We open the tab synchronously (before the await) so popup
// blockers don't eat it, then point it at the blob once it arrives.
async function openReceipt(id, onError) {
  const win = window.open('', '_blank');
  try {
    const r = await axios.get(`/api/time/expenses/${id}/receipt`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    if (win) win.location = url;
    else {
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } catch (e) {
    if (win) win.close();
    if (onError) onError('Could not open that receipt.');
  }
}

// Open an attached contractor invoice through the authenticated API in a
// new tab (same popup-safe pattern as openReceipt).
async function openInvoice(id, onError) {
  const win = window.open('', '_blank');
  try {
    const r = await axios.get(`/api/contractor-payments/${id}/invoice`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    if (win) win.location = url;
    else {
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } catch (e) {
    if (win) win.close();
    if (onError) onError('Could not open that invoice.');
  }
}

// ── Pay-week date helpers (local time, no timezone shift) ────────────
// Payroll runs on Friday for the previous Monday–Sunday week.
function isoToDate(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); }
function dateToISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDaysISO(s, n) { const d = isoToDate(s); d.setDate(d.getDate() + n); return dateToISO(d); }
// Friday of the current (Monday-based) week.
function thisWeekFridayISO() {
  const d = new Date();
  const mondayOffset = (d.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(d); monday.setDate(d.getDate() - mondayOffset);
  const fri = new Date(monday); fri.setDate(monday.getDate() + 4);
  return dateToISO(fri);
}
// The Mon–Sun pay week that a Friday payday covers (the completed week
// before the payday's own week). Robust to any payday date.
function payWeekFor(paydayISO) {
  const d = isoToDate(paydayISO);
  const mondayOffset = (d.getDay() + 6) % 7;
  const mondayOfPayWeek = new Date(d); mondayOfPayWeek.setDate(d.getDate() - mondayOffset);
  const start = new Date(mondayOfPayWeek); start.setDate(mondayOfPayWeek.getDate() - 7); // previous Monday
  const end = new Date(start); end.setDate(start.getDate() + 6); // that Sunday
  return { start: dateToISO(start), end: dateToISO(end) };
}
function fmtNice(iso) {
  return isoToDate(iso).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}
// Month helpers for the Contractor Payments month picker.
function monthKey(y, m) { return `${y}-${String(m + 1).padStart(2, '0')}`; }
function monthStartISO(key) { return `${key}-01`; }
function monthEndISO(key) {
  const [y, m] = key.split('-').map(Number);
  const last = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this one
  return `${key}-${String(last).padStart(2, '0')}`;
}
function recentMonths(n) {
  const out = [];
  const d = new Date(); d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push({ key: monthKey(d.getFullYear(), d.getMonth()), label: d.toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}
// Sunday of the week containing `date` (weeks run Sunday–Saturday).
function sundayOfWeek(date) {
  const off = date.getDay(); // 0 = Sunday
  const s = new Date(date); s.setDate(date.getDate() - off); return s;
}
// Previous week (Sun–Sat) — the usual payroll week.
function lastWeekRange() {
  const s = sundayOfWeek(new Date());
  const ls = new Date(s); ls.setDate(s.getDate() - 7);   // previous Sunday
  const le = new Date(ls); le.setDate(ls.getDate() + 6); // that Saturday
  return { from: dateToISO(ls), to: dateToISO(le) };
}
// Current week (Sun–Sat).
function thisWeekRange() {
  const s = sundayOfWeek(new Date());
  const e = new Date(s); e.setDate(s.getDate() + 6);     // Saturday
  return { from: dateToISO(s), to: dateToISO(e) };
}

// ── Week grouping for the staff's own lists ──────────────────────────
// Entries are grouped into Monday–Sunday weeks (the pay week). Finished
// weeks show as a single totals line; the current week shows its entries.
function groupByWeek(items, dateField) {
  const map = new Map();
  for (const it of items) {
    const iso = it[dateField];
    if (!iso) continue;
    const d = isoToDate(iso);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // back to Monday
    const start = dateToISO(d);
    if (!map.has(start)) map.set(start, { start, end: addDaysISO(start, 6), items: [] });
    map.get(start).items.push(it);
  }
  return [...map.values()].sort((a, b) => (a.start < b.start ? 1 : -1));
}
function weekLabel(w) {
  const f = (iso) => isoToDate(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  return `${f(w.start)} – ${f(w.end)}`;
}
function isWeekDone(w) { return w.end < todayISO(); }
// ── Date range for the staff's own lists ─────────────────────────────
function mondayISO(offsetWeeks = 0) {
  const d = isoToDate(todayISO());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offsetWeeks * 7);
  return dateToISO(d);
}
const RANGE_PRESETS = [
  { key: '60d', label: 'Last 60 days', get: () => ({ from: daysAgoISO(60), to: todayISO() }) },
  { key: 'thisweek', label: 'This week', get: () => ({ from: mondayISO(0), to: addDaysISO(mondayISO(0), 6) }) },
  { key: 'lastweek', label: 'Last week', get: () => ({ from: mondayISO(-1), to: addDaysISO(mondayISO(-1), 6) }) },
  { key: 'thismonth', label: 'This month', get: () => ({ from: `${todayISO().slice(0, 7)}-01`, to: todayISO() }) },
  {
    key: 'lastmonth', label: 'Last month', get: () => {
      const d = isoToDate(`${todayISO().slice(0, 7)}-01`); d.setDate(0); // last day of previous month
      const to = dateToISO(d);
      return { from: `${to.slice(0, 7)}-01`, to };
    },
  },
  { key: '90d', label: 'Last 90 days', get: () => ({ from: daysAgoISO(90), to: todayISO() }) },
];
function rangeLabel(r) {
  const preset = RANGE_PRESETS.find((p) => { const v = p.get(); return v.from === r.from && v.to === r.to; });
  if (preset) return preset.label.toLowerCase();
  return `${fmtNice(r.from)} – ${fmtNice(r.to)}`;
}
function RangePicker({ range, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
      <label className="form-field">
        <span>Show from</span>
        <input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && onChange({ ...range, from: e.target.value })} />
      </label>
      <label className="form-field">
        <span>To</span>
        <input type="date" value={range.to} min={range.from} onChange={(e) => e.target.value && onChange({ ...range, to: e.target.value })} />
      </label>
      {RANGE_PRESETS.map((p) => {
        const v = p.get();
        const on = v.from === range.from && v.to === range.to;
        return (
          <button key={p.key} type="button" className={on ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'} onClick={() => onChange(v)}>
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// Open/closed state per week: the current week starts open; every other
// week starts as its summary line. Click a week to show or hide its entries.
function useWeekToggle() {
  const [overrides, setOverrides] = useState({}); // start -> bool
  const isOpen = (w) => (w.start in overrides ? overrides[w.start] : !isWeekDone(w));
  const toggle = (w) => setOverrides((p) => ({ ...p, [w.start]: !isOpen(w) }));
  return { isOpen, toggle };
}
const weekRowStyle = {
  background: 'var(--color-surface-sunken)', cursor: 'pointer', fontWeight: 600,
};

export default function TimeTracking() {
  const { user } = useAuth();
  const isManager = user?.role === 'admin';
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('hours'); // mobile only: 'hours' | 'expenses'

  const [workDate, setWorkDate] = useState(todayISO());
  const [hours, setHours] = useState('');
  const [entryType, setEntryType] = useState('regular');
  const [myEntries, setMyEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Date range for the lists below (hours and expenses share it).
  const [range, setRange] = useState(() => RANGE_PRESETS[0].get());

  const loadMine = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/time/mine', { params: { from: range.from, to: range.to } });
      setMyEntries(r.data.entries || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => { loadMine(); }, [loadMine]);

  // When the date changes, show the already-logged hours for that day (if
  // any) so saving a day a second time edits rather than surprises.
  useEffect(() => {
    const existing = myEntries.find((e) => e.work_date === workDate);
    setHours(existing ? String(existing.hours) : '');
    setEntryType(existing?.entry_type || 'regular');
  }, [workDate, myEntries]);

  async function saveHours(e) {
    e.preventDefault();
    setError(null);
    const h = Number(hours);
    if (!Number.isFinite(h) || h < 0 || h > 24) return setError('Enter hours between 0 and 24.');
    setSaving(true);
    try {
      await axios.post('/api/time', { work_date: workDate, hours: h, entry_type: entryType });
      await loadMine();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteMine(id) {
    if (!window.confirm('Delete this entry?')) return;
    setError(null);
    try {
      await axios.delete(`/api/time/${id}`);
      await loadMine();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  const myTotal = useMemo(
    () => round2(myEntries.reduce((a, e) => a + (Number(e.hours) || 0), 0)),
    [myEntries]
  );
  const hourWeeks = useMemo(() => groupByWeek(myEntries, 'work_date'), [myEntries]);
  const hoursToggle = useWeekToggle();
  const weekHours = (w) => {
    const b = hourBreakdown(w.items);
    return { ...b, days: w.items.length };
  };
  const hoursSummary = (w) => {
    const t = weekHours(w);
    const mix = breakdownText(t);
    return `${t.total} h${mix !== 'Regular' ? ` (${mix})` : ''} · ${t.days} day${t.days === 1 ? '' : 's'}`;
  };

  // On a phone each tab shows one thing; on desktop everything stacks.
  const showHours = !isMobile || tab === 'hours';
  const showExpenses = !isMobile || tab === 'expenses';

  return (
    <section className="page">
      <style>{MOBILE_CSS}</style>
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <div>
          <h2 style={{ margin: 0 }}>Time &amp; Expenses</h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            Log your hours and expenses for each day.
          </div>
        </div>
      </div>

      <RangePicker range={range} onChange={setRange} />

      {isMobile && (
        <div className="tt-tabs" role="tablist">
          <button type="button" className={`tt-tab${tab === 'hours' ? ' is-active' : ''}`} onClick={() => setTab('hours')}>Hours</button>
          <button type="button" className={`tt-tab${tab === 'expenses' ? ' is-active' : ''}`} onClick={() => setTab('expenses')}>Expenses</button>
        </div>
      )}

      {error && <p className="error" style={{ color: 'var(--color-bad)' }}>{error}</p>}

      {showHours && (
        <>
          <form
            onSubmit={saveHours}
            className="tt-form"
            style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}
          >
            <label className="form-field">
              <span>Date</span>
              <input type="date" value={workDate} max={todayISO()} onChange={(e) => setWorkDate(e.target.value)} />
            </label>
            <label className="form-field">
              <span>Hours</span>
              <input
                type="number"
                step="0.25"
                min="0"
                max="24"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g. 8"
                className="cell-mono"
              />
            </label>
            <label className="form-field">
              <span>Type</span>
              <select value={entryType} onChange={(e) => setEntryType(e.target.value)}>
                {HOUR_TYPES.map((t) => <option key={t.key} value={t.key}>{t.option}</option>)}
              </select>
            </label>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save hours'}
            </button>
          </form>

          <h3 style={{ marginBottom: 'var(--space-2)' }}>
            Recent entries
            {myEntries.length > 0 && (
              <span style={{ fontWeight: 400, color: 'var(--color-text-dim)', fontSize: 13 }}>
                {' '}· {myTotal} h, {rangeLabel(range)}
              </span>
            )}
          </h3>

          {isMobile ? (
            loading ? (
              <div className="tt-empty">Loading…</div>
            ) : myEntries.length === 0 ? (
              <div className="tt-empty">No hours logged yet.</div>
            ) : (
              <ul className="tt-cards">
                {hourWeeks.map((w) => (
                  <Fragment key={w.start}>
                    <li className="tt-card" style={weekRowStyle} onClick={() => hoursToggle.toggle(w)}>
                      <div>
                        <div className="tt-card-title">{hoursToggle.isOpen(w) ? '\u25be' : '\u25b8'} Week of {weekLabel(w)}</div>
                        <div className="tt-card-sub">{hoursSummary(w)}{isWeekDone(w) ? '' : ' · this week'}</div>
                      </div>
                    </li>
                    {hoursToggle.isOpen(w) && w.items.map((e) => (
                      <li className="tt-card" key={e.id}>
                        <div>
                          <div className="tt-card-title cell-mono">{e.hours} h</div>
                          <div className="tt-card-sub">{fmtNice(e.work_date)} · {typeLabel(e.entry_type)}</div>
                        </div>
                        <div className="tt-card-actions">
                          <button type="button" className="btn-secondary btn-sm" onClick={() => setWorkDate(e.work_date)}>Edit</button>
                          <button type="button" className="btn-secondary btn-sm" style={{ color: 'var(--color-bad, #b91c1c)' }} onClick={() => deleteMine(e.id)}>Delete</button>
                        </div>
                      </li>
                    ))}
                  </Fragment>
                ))}
              </ul>
            )
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Date</th><th>Days</th><th>Hours</th><th title="Regular = hours worked. Holiday = paid statutory holiday. Vacation = paid time off.">Type</th><th aria-label="Actions"></th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5}>Loading…</td></tr>
                  ) : myEntries.length === 0 ? (
                    <tr><td colSpan={5} className="cell-muted">No hours logged yet.</td></tr>
                  ) : (
                    hourWeeks.map((w) => (
                      <Fragment key={w.start}>
                        <tr style={weekRowStyle} onClick={() => hoursToggle.toggle(w)} title={hoursToggle.isOpen(w) ? 'Hide this week' : 'Show this week'}>
                          <td>
                            {hoursToggle.isOpen(w) ? '\u25be' : '\u25b8'} Week of {weekLabel(w)}
                            {!isWeekDone(w) && <span style={{ fontWeight: 400, marginLeft: 8, color: 'var(--color-text-dim)' }}>· this week</span>}
                          </td>
                          <td className="cell-mono">{weekHours(w).days} day{weekHours(w).days === 1 ? '' : 's'}</td>
                          <td className="cell-mono">{weekHours(w).total} h</td>
                          <td style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>
                            {breakdownText(weekHours(w))}
                          </td>
                          <td />
                        </tr>
                        {hoursToggle.isOpen(w) && w.items.map((e) => (
                      <tr key={e.id}>
                        <td className="cell-mono" style={{ paddingLeft: 28 }}>{e.work_date}</td>
                        <td style={{ color: 'var(--color-text-dim)' }}>{isoToDate(e.work_date).toLocaleDateString('en-CA', { weekday: 'short' })}</td>
                        <td className="cell-mono">{e.hours}</td>
                        <td>{typeLabel(e.entry_type)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setWorkDate(e.work_date)}
                            title="Load this day into the form above to change it"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            style={{ marginLeft: 6, color: 'var(--color-bad, #b91c1c)' }}
                            onClick={() => deleteMine(e.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                        ))}
                      </Fragment>
                    ))
                  )}
                </tbody>
                {!loading && myEntries.length > 0 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>
                      <td>Total · {rangeLabel(range)}</td>
                      <td className="cell-mono">{myEntries.length} day{myEntries.length === 1 ? '' : 's'}</td>
                      <td className="cell-mono">{myTotal} h</td>
                      <td style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>
                        {(() => {
                          return breakdownText(hourBreakdown(myEntries));
                        })()}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}

      {showExpenses && <MyExpenses isMobile={isMobile} range={range} />}

      {!isMobile && isManager && <PayrollView />}
      {!isMobile && isManager && <ContractorPayments />}
      {!isMobile && isManager && <ManagerView />}
    </section>
  );
}

// Payroll view: a date range (defaults to last week, Mon–Fri) with quick
// buttons, showing each employee's regular/holiday/total hours and the
// expenses to reimburse, plus a CSV. Reuses the existing /all endpoints.
function PayrollView() {
  const initial = lastWeekRange();
  const [start, setStart] = useState(initial.from);
  const [end, setEnd] = useState(initial.to);
  const [hoursRows, setHoursRows] = useState([]);
  const [expRows, setExpRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, e] = await Promise.all([
        axios.get('/api/time/all', { params: { from: start, to: end } }),
        axios.get('/api/time/expenses/all', { params: { from: start, to: end } }),
      ]);
      setHoursRows(h.data.entries || []);
      setExpRows(e.data.expenses || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const byEmployee = useMemo(() => {
    const m = new Map();
    for (const r of hoursRows) {
      const o = m.get(r.user_name) || { regular: 0, holiday: 0, vacation: 0, expenses: 0 };
      const k = (r.entry_type === 'holiday' || r.entry_type === 'vacation') ? r.entry_type : 'regular';
      o[k] += Number(r.hours) || 0;
      m.set(r.user_name, o);
    }
    for (const r of expRows) {
      const o = m.get(r.user_name) || { regular: 0, holiday: 0, vacation: 0, expenses: 0 };
      o.expenses += Number(r.amount) || 0;
      m.set(r.user_name, o);
    }
    return Array.from(m.entries())
      .map(([name, o]) => ({
        name,
        regular: round2(o.regular),
        holiday: round2(o.holiday),
        vacation: round2(o.vacation),
        total: round2(o.regular + o.holiday + o.vacation),
        expenses: round2(o.expenses),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [hoursRows, expRows]);

  const totalRegular = round2(byEmployee.reduce((a, e) => a + e.regular, 0));
  const totalHoliday = round2(byEmployee.reduce((a, e) => a + e.holiday, 0));
  const totalVacation = round2(byEmployee.reduce((a, e) => a + e.vacation, 0));
  const totalHours = round2(byEmployee.reduce((a, e) => a + e.total, 0));
  const totalExpenses = round2(byEmployee.reduce((a, e) => a + e.expenses, 0));

  function downloadCsv() {
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [['Employee', 'From', 'To', 'Regular hours', 'Holiday hours', 'Vacation hours', 'Total hours', 'Total expenses'].join(',')];
    for (const e of byEmployee) lines.push([esc(e.name), esc(start), esc(end), esc(e.regular), esc(e.holiday), esc(e.vacation), esc(e.total), esc(e.expenses)].join(','));
    const csv = lines.join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_${start}_to_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
      <h2 style={{ marginBottom: 4 }}>Payroll — weekly</h2>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginBottom: 'var(--space-3)' }}>
        Defaults to last week (Sun–Sat). Use the buttons or pick any date range.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
        <button type="button" className="btn-secondary" onClick={() => { const w = lastWeekRange(); setStart(w.from); setEnd(w.to); }}>Last week</button>
        <button type="button" className="btn-secondary" onClick={() => { const w = thisWeekRange(); setStart(w.from); setEnd(w.to); }}>This week</button>
        <label className="form-field">
          <span>From</span>
          <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="form-field">
          <span>To</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={downloadCsv} disabled={byEmployee.length === 0}>Download payroll CSV</button>
      </div>

      <p className="modal-sub" style={{ marginTop: 0 }}>
        Showing: <strong>{fmtNice(start)} → {fmtNice(end)}</strong>
        {loading && <span style={{ color: 'var(--color-text-dim)' }}> · loading…</span>}
      </p>

      {error && <p className="error" style={{ color: 'var(--color-bad)' }}>{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>Employee</th><th>Regular</th><th>Holiday</th><th>Vacation</th><th>Total hrs</th><th>Expenses (reimburse)</th></tr>
          </thead>
          <tbody>
            {byEmployee.length === 0 ? (
              <tr><td colSpan={6} className="cell-muted">No hours or expenses logged for this range.</td></tr>
            ) : (
              <>
                {byEmployee.map((e) => (
                  <tr key={e.name}>
                    <td>{e.name}</td>
                    <td className="cell-mono">{e.regular}</td>
                    <td className="cell-mono">{e.holiday}</td>
                    <td className="cell-mono">{e.vacation}</td>
                    <td className="cell-mono">{e.total}</td>
                    <td className="cell-mono">{money(e.expenses)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td>All employees</td>
                  <td className="cell-mono">{totalRegular}</td>
                  <td className="cell-mono">{totalHoliday}</td>
                  <td className="cell-mono">{totalVacation}</td>
                  <td className="cell-mono">{totalHours}</td>
                  <td className="cell-mono">{money(totalExpenses)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A user's own expenses — many per day (lunch, gas, parking, …). Each
// expense can optionally carry one receipt (PDF or image).
function MyExpenses({ isMobile, range }) {
  const [expenseDate, setExpenseDate] = useState(todayISO());
  const [category, setCategory] = useState(EXPENSE_CATEGORIES[0]);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Receipt state. `receipt` is a freshly-picked file ({ data, mime,
  // name }); `existingReceipt` marks that the row being edited already
  // has one; `removeReceipt` requests deleting it. fileInputKey lets us
  // reset the <input type=file> by remounting it.
  const [receipt, setReceipt] = useState(null);
  const [existingReceipt, setExistingReceipt] = useState(false);
  const [removeReceipt, setRemoveReceipt] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [reading, setReading] = useState(false);
  const [info, setInfo] = useState(null);
  const receiptRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/time/expenses/mine', { params: { from: range.from, to: range.to } });
      setRows(r.data.expenses || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  function resetForm() {
    setEditingId(null);
    setExpenseDate(todayISO());
    setCategory(EXPENSE_CATEGORIES[0]);
    setAmount('');
    setDescription('');
    setReceipt(null);
    setExistingReceipt(false);
    setRemoveReceipt(false);
    setFileInputKey((k) => k + 1);
    setReading(false);
    setInfo(null);
  }
  function startEdit(r) {
    setError(null);
    setInfo(null);
    setEditingId(r.id);
    setExpenseDate(r.expense_date);
    setCategory(EXPENSE_CATEGORIES.includes(r.category) ? r.category : 'Other');
    setAmount(String(r.amount));
    setDescription(r.description || '');
    setReceipt(null);
    setExistingReceipt(!!r.has_receipt);
    setRemoveReceipt(false);
    setFileInputKey((k) => k + 1);
    if (isMobile && typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function onPickReceipt(e) { handleReceipt(e.target.files && e.target.files[0]); }
  function onDropReceipt(e) {
    e.preventDefault();
    handleReceipt(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
  }
  function handleReceipt(f) {
    setError(null);
    setInfo(null);
    if (!f) { setReceipt(null); return; }
    const isImage = (f.type || '').startsWith('image/');
    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (!isImage && !isPdf) { setError('Receipt must be a PDF or an image.'); setReceipt(null); return; }
    if (f.size > RECEIPT_MAX_BYTES) { setError('Receipt is too large (max 10 MB).'); setReceipt(null); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const picked = { data: String(reader.result), mime: f.type || (isPdf ? 'application/pdf' : 'application/octet-stream'), name: f.name };
      setReceipt(picked);
      setRemoveReceipt(false);
      autofillFromReceipt(picked);
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsDataURL(f);
  }
  // Ask the server to read the receipt and pre-fill the fields. Never
  // blocks manual entry; stays quiet if AI reading isn't set up (501).
  async function autofillFromReceipt(picked) {
    setReading(true);
    try {
      const r = await axios.post('/api/time/expenses/parse-receipt', {
        receipt: { data: picked.data, mime: picked.mime, filename: picked.name },
      });
      const x = r.data.extracted || {};
      if (x.amount != null) setAmount(String(x.amount));
      if (x.expense_date) setExpenseDate(x.expense_date);
      if (x.category && EXPENSE_CATEGORIES.includes(x.category)) setCategory(x.category);
      if (x.description) setDescription((d) => d || x.description);
      if (x.currency && String(x.currency).toUpperCase() !== 'CAD') {
        setInfo(`Filled from the receipt — currency looks like ${x.currency}, double-check the amount.`);
      } else {
        setInfo('Filled from the receipt — review and Save.');
      }
    } catch (e) {
      const status = e.response?.status;
      if (status && status !== 501) setInfo('Couldn’t auto-read this one — just fill it in by hand.');
      // 501 = AI reading not set up yet: stay quiet; the receipt still attaches.
    } finally {
      setReading(false);
    }
  }

  async function saveExpense(e) {
    e.preventDefault();
    setError(null);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) return setError('Enter an amount of 0 or more.');
    setSaving(true);
    try {
      const body = { expense_date: expenseDate, category, amount: amt, description };
      if (receipt) body.receipt = { data: receipt.data, mime: receipt.mime, filename: receipt.name };
      else if (editingId && removeReceipt) body.remove_receipt = true;
      if (editingId) await axios.put(`/api/time/expenses/${editingId}`, body);
      else await axios.post('/api/time/expenses', body);
      resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function del(id) {
    if (!window.confirm('Delete this expense?')) return;
    setError(null);
    try {
      await axios.delete(`/api/time/expenses/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  const total = useMemo(() => round2(rows.reduce((a, r) => a + (Number(r.amount) || 0), 0)), [rows]);
  const expWeeks = useMemo(() => groupByWeek(rows, 'expense_date'), [rows]);
  const expToggle = useWeekToggle();
  const expSummary = (w) => {
    const sum = round2(w.items.reduce((a, r) => a + (Number(r.amount) || 0), 0));
    return `${money(sum)} · ${w.items.length} expense${w.items.length === 1 ? '' : 's'}`;
  };

  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
      <h2 style={{ marginBottom: 4 }}>My Expenses</h2>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginBottom: 'var(--space-3)' }}>
        Log expenses as they happen — lunch, gas, parking, etc. Add as many per day as you need, and attach a receipt if you have one.
      </div>

      {error && <p className="error" style={{ color: 'var(--color-bad)' }}>{error}</p>}
      {info && <p style={{ color: 'var(--color-good, #0d9488)', fontSize: 13, margin: '0 0 8px' }}>{info}</p>}

      <form onSubmit={saveExpense} className="tt-form" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <label className="form-field">
          <span>Date</span>
          <input type="date" value={expenseDate} max={todayISO()} onChange={(e) => setExpenseDate(e.target.value)} />
        </label>
        <label className="form-field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="form-field">
          <span>Amount ($)</span>
          <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="cell-mono" />
        </label>
        <label className="form-field" style={{ flex: '1 1 220px' }}>
          <span>Description (optional)</span>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Lunch with customer" />
        </label>
        <div className="form-field" style={{ flex: '1 1 260px' }}>
          <span>Receipt (optional) — PDF or photo</span>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropReceipt}
            onClick={() => receiptRef.current && receiptRef.current.click()}
            role="button"
            tabIndex={0}
            style={{
              border: '1.5px dashed var(--color-accent, #3b7cb5)', borderRadius: 10,
              background: 'rgba(59,124,181,.06)', padding: '10px 12px', cursor: 'pointer',
              fontSize: 13, color: 'var(--color-text, #0f172a)', minHeight: 44, display: 'flex', alignItems: 'center',
            }}
          >
            {reading ? 'Reading receipt…' : receipt ? `📎 ${receipt.name}` : '📄 Drop or tap to add — it reads the receipt and fills the fields'}
          </div>
          <input ref={receiptRef} key={fileInputKey} type="file" accept={RECEIPT_ACCEPT} style={{ display: 'none' }} onChange={onPickReceipt} />
        </div>
        <button type="submit" className="btn-primary" disabled={saving || reading}>
          {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add expense')}
        </button>
        {editingId && (
          <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
            Cancel
          </button>
        )}
      </form>

      {/* Edit mode: show the current receipt and let the user view, keep,
          replace (by picking a new file above), or remove it. */}
      {editingId && existingReceipt && !receipt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: '-8px', marginBottom: 'var(--space-4)', fontSize: 13 }}>
          <span style={{ color: 'var(--color-text-dim)' }}>This expense has a receipt.</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => openReceipt(editingId, setError)}>View current</button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-bad, #b91c1c)' }}>
            <input type="checkbox" checked={removeReceipt} onChange={(e) => setRemoveReceipt(e.target.checked)} />
            Remove it
          </label>
          <span style={{ color: 'var(--color-text-dim)' }}>Or pick a new file above to replace it.</span>
        </div>
      )}
      {editingId && receipt && (
        <div style={{ marginTop: '-8px', marginBottom: 'var(--space-4)', fontSize: 13, color: 'var(--color-text-dim)' }}>
          New receipt selected ({receipt.name}) — it will {existingReceipt ? 'replace the current one' : 'be attached'} when you save.
        </div>
      )}

      <h3 style={{ marginBottom: 'var(--space-2)' }}>
        Recent expenses
        {rows.length > 0 && (
          <span style={{ fontWeight: 400, color: 'var(--color-text-dim)', fontSize: 13 }}>
            {' '}· {money(total)}, {rangeLabel(range)}
          </span>
        )}
      </h3>

      {isMobile ? (
        loading ? (
          <div className="tt-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="tt-empty">No expenses logged yet.</div>
        ) : (
          <ul className="tt-cards">
            {expWeeks.map((w) => (
              <Fragment key={w.start}>
                <li className="tt-card" style={weekRowStyle} onClick={() => expToggle.toggle(w)}>
                  <div>
                    <div className="tt-card-title">{expToggle.isOpen(w) ? '\u25be' : '\u25b8'} Week of {weekLabel(w)}</div>
                    <div className="tt-card-sub">{expSummary(w)}{isWeekDone(w) ? '' : ' · this week'}</div>
                  </div>
                </li>
            {expToggle.isOpen(w) && w.items.map((r) => (
              <li className="tt-card" key={r.id}>
                <div>
                  <div className="tt-card-title cell-mono">{money(r.amount)}</div>
                  <div className="tt-card-sub">
                    {fmtNice(r.expense_date)} · {r.category}
                    {r.description ? ` · ${r.description}` : ''}
                  </div>
                </div>
                <div className="tt-card-actions">
                  {r.has_receipt && (
                    <button type="button" className="btn-secondary btn-sm" onClick={() => openReceipt(r.id, setError)}>Receipt</button>
                  )}
                  <button type="button" className="btn-secondary btn-sm" onClick={() => startEdit(r)}>Edit</button>
                  <button type="button" className="btn-secondary btn-sm" style={{ color: 'var(--color-bad, #b91c1c)' }} onClick={() => del(r.id)}>Delete</button>
                </div>
              </li>
            ))}
              </Fragment>
            ))}
          </ul>
        )
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Category</th><th>Amount</th><th>Description</th><th>Receipt</th><th aria-label="Actions"></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="cell-muted">No expenses logged yet.</td></tr>
              ) : (
                expWeeks.map((w) => (
                  <Fragment key={w.start}>
                    <tr style={weekRowStyle} onClick={() => expToggle.toggle(w)} title={expToggle.isOpen(w) ? 'Hide this week' : 'Show this week'}>
                      <td>
                        {expToggle.isOpen(w) ? '\u25be' : '\u25b8'} Week of {weekLabel(w)}
                        {!isWeekDone(w) && <span style={{ fontWeight: 400, marginLeft: 8, color: 'var(--color-text-dim)' }}>· this week</span>}
                      </td>
                      <td style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>{w.items.length} expense{w.items.length === 1 ? '' : 's'}</td>
                      <td className="cell-mono">{money(w.items.reduce((a, r) => a + (Number(r.amount) || 0), 0))}</td>
                      <td colSpan={3} />
                    </tr>
                    {expToggle.isOpen(w) && w.items.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-mono" style={{ paddingLeft: 28 }}>{r.expense_date}</td>
                    <td>{r.category}</td>
                    <td className="cell-mono">{money(r.amount)}</td>
                    <td>{r.description || ''}</td>
                    <td>
                      {r.has_receipt
                        ? <button type="button" className="btn-secondary btn-sm" onClick={() => openReceipt(r.id, setError)}>View</button>
                        : <span className="cell-muted">—</span>}
                    </td>
                    <td>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => startEdit(r)}>
                        Edit
                      </button>
                      <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: 6, color: 'var(--color-bad, #b91c1c)' }} onClick={() => del(r.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                    ))}
                  </Fragment>
                ))
              )}
            </tbody>
            {!loading && rows.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>
                  <td>Total · {rangeLabel(range)}</td>
                  <td style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>{rows.length} expense{rows.length === 1 ? '' : 's'}</td>
                  <td className="cell-mono">{money(total)}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// Admin-only: contractor / vendor payouts (offshore developers, Jose,
// etc.) the owner enters by hand, usually every two weeks. Not tied to
// employee hours — a simple paid-out ledger that records the pay period
// each payment covers. All amounts are CAD.
function ContractorPayments() {
  const [from, setFrom] = useState(() => daysAgoISO(90));
  const [to, setTo] = useState(todayISO());
  const [months, setMonths] = useState([]); // selected 'YYYY-MM' keys
  const MONTH_OPTS = useMemo(() => recentMonths(60), []);
  const YEAR_OPTS = useMemo(() => Array.from(new Set(MONTH_OPTS.map((m) => m.key.slice(0, 4)))), [MONTH_OPTS]);
  // Default: the whole current year (by the period each payment covers).
  const [year, setYear] = useState(() => todayISO().slice(0, 4));
  const [payeeFilter, setPayeeFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [knownPayees, setKnownPayees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [payee, setPayee] = useState('');
  const [amount, setAmount] = useState('');
  const [payDate, setPayDate] = useState(todayISO());
  const [coversFrom, setCoversFrom] = useState('');
  const [coversTo, setCoversTo] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Invoice attachment + auto-read.
  const [invoiceFile, setInvoiceFile] = useState(null); // { data, mime, name }
  const [existingInvoice, setExistingInvoice] = useState(false);
  const [removeInvoice, setRemoveInvoice] = useState(false);
  const [reading, setReading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // When months are picked, filter by the COVERS period (overlap),
      // not the pay date; otherwise use the Paid-from/to range.
      let params;
      if (months.length) {
        const sorted = [...months].sort();
        params = { covers_from: monthStartISO(sorted[0]), covers_to: monthEndISO(sorted[sorted.length - 1]) };
      } else if (year !== 'all') {
        params = { covers_from: `${year}-01-01`, covers_to: `${year}-12-31` };
      } else {
        params = { from, to };
      }
      const r = await axios.get('/api/contractor-payments', { params });
      setRows(r.data.payments || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, months, year]);
  useEffect(() => { load(); }, [load]);

  const loadPayees = useCallback(async () => {
    try {
      const r = await axios.get('/api/contractor-payments/payees');
      setKnownPayees(r.data.payees || []);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { loadPayees(); }, [loadPayees]);

  function resetForm() {
    setEditingId(null);
    setPayee('');
    setAmount('');
    setPayDate(todayISO());
    setCoversFrom('');
    setCoversTo('');
    setInvoiceNumber('');
    setInvoiceDate('');
    setNote('');
    setInvoiceFile(null);
    setExistingInvoice(false);
    setRemoveInvoice(false);
    setInfo(null);
    if (fileRef.current) fileRef.current.value = '';
  }
  function startEdit(r) {
    setError(null);
    setInfo(null);
    setEditingId(r.id);
    setPayee(r.payee);
    setAmount(String(r.amount));
    setPayDate(r.pay_date);
    setCoversFrom(r.period_start || '');
    setCoversTo(r.period_end || '');
    setInvoiceNumber(r.invoice_number || '');
    setInvoiceDate(r.invoice_date || '');
    setNote(r.note || '');
    setInvoiceFile(null);
    setExistingInvoice(!!r.has_invoice);
    setRemoveInvoice(false);
    if (fileRef.current) fileRef.current.value = '';
  }
  // Fill the "covers" period as the two weeks ending on the pay date.
  function fillTwoWeeks() {
    const end = payDate || todayISO();
    setCoversTo(end);
    setCoversFrom(addDaysISO(end, -13));
  }

  // Read a chosen/dropped invoice, attach it, and ask the server to
  // auto-fill the fields from it.
  function handleInvoiceFile(f) {
    if (!f) return;
    setError(null);
    setInfo(null);
    const isImage = (f.type || '').startsWith('image/');
    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (!isImage && !isPdf) { setError('Invoice must be a PDF or an image.'); return; }
    if (f.size > 10 * 1024 * 1024) { setError('Invoice is too large (max 10 MB).'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const picked = {
        data: String(reader.result),
        mime: f.type || (isPdf ? 'application/pdf' : 'application/octet-stream'),
        name: f.name,
      };
      setInvoiceFile(picked);
      setRemoveInvoice(false);
      autofillFromInvoice(picked);
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsDataURL(f);
  }
  async function autofillFromInvoice(picked) {
    setReading(true);
    setError(null);
    setInfo(null);
    try {
      const r = await axios.post('/api/contractor-payments/parse-invoice', {
        invoice: { data: picked.data, mime: picked.mime, filename: picked.name },
      });
      const x = r.data.extracted || {};
      if (x.payee) setPayee(x.payee);
      if (x.amount != null) setAmount(String(x.amount));
      if (x.period_start) setCoversFrom(x.period_start);
      if (x.period_end) setCoversTo(x.period_end);
      if (x.invoice_number) setInvoiceNumber(x.invoice_number);
      if (x.invoice_date) setInvoiceDate(x.invoice_date);
      if (x.summary) setNote((n) => n || x.summary);
      const dup = x.invoice_number
        ? rows.find((r) => r.id !== editingId
            && (r.invoice_number || '') === String(x.invoice_number)
            && (r.payee || '').toLowerCase() === String(x.payee || '').toLowerCase())
        : null;
      if (dup) {
        setInfo(null);
        setError(`⚠ Duplicate invoice — #${x.invoice_number} for ${x.payee} is already recorded (pay date ${dup.pay_date}). You can't add it twice.`);
      } else if (x.currency && String(x.currency).toUpperCase() !== 'CAD') {
        setInfo(`Filled from the invoice — but the currency looks like ${x.currency}, not CAD. Double-check the amount before saving.`);
      } else {
        setInfo('Filled from the invoice — review the fields and Save.');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read the invoice automatically — you can still fill it in by hand.');
    } finally {
      setReading(false);
    }
  }
  function onDropInvoice(e) {
    e.preventDefault();
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    handleInvoiceFile(f);
  }

  // Local duplicate guard (the server enforces it too). Matches an
  // already-loaded payment with the same payee + invoice number.
  function localDuplicate(payeeVal, inv) {
    const i = (inv || '').trim();
    if (!i) return null;
    const p = (payeeVal || '').trim().toLowerCase();
    return rows.find((r) => r.id !== editingId && (r.invoice_number || '') === i && (r.payee || '').toLowerCase() === p) || null;
  }

  async function save(e) {
    e.preventDefault();
    setError(null);
    if (!payee.trim()) return setError('Enter who you paid.');
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) return setError('Enter an amount of 0 or more.');
    if (coversFrom && coversTo && coversFrom > coversTo) return setError('Covers "from" must be on or before "to".');
    const dup = localDuplicate(payee, invoiceNumber);
    if (dup) return setError(`Invoice ${invoiceNumber.trim()} for ${payee.trim()} is already recorded (pay date ${dup.pay_date}). You can't add it twice.`);
    setSaving(true);
    try {
      const body = {
        payee: payee.trim(),
        amount: amt,
        pay_date: payDate,
        period_start: coversFrom || null,
        period_end: coversTo || null,
        invoice_number: invoiceNumber.trim() || null,
        invoice_date: invoiceDate || null,
        note,
      };
      if (invoiceFile) body.invoice = { data: invoiceFile.data, mime: invoiceFile.mime, filename: invoiceFile.name };
      else if (editingId && removeInvoice) body.remove_invoice = true;
      if (editingId) await axios.put(`/api/contractor-payments/${editingId}`, body);
      else await axios.post('/api/contractor-payments', body);
      resetForm();
      await Promise.all([load(), loadPayees()]);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function del(id) {
    if (!window.confirm('Delete this payment?')) return;
    setError(null);
    try {
      await axios.delete(`/api/contractor-payments/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  // When one or more months are picked, show only those months.
  const filteredRows = useMemo(() => {
    let list = rows;
    if (months.length) {
      list = list.filter((r) => {
        const ps = r.period_start || r.pay_date || '';
        const pe = r.period_end || r.pay_date || '';
        return months.some((k) => ps <= monthEndISO(k) && pe >= monthStartISO(k));
      });
    } else if (year !== 'all') {
      list = list.filter((r) => {
        const ps = r.period_start || r.pay_date || '';
        const pe = r.period_end || r.pay_date || '';
        return ps <= `${year}-12-31` && pe >= `${year}-01-01`;
      });
    }
    if (payeeFilter !== 'all') list = list.filter((r) => r.payee === payeeFilter);
    return list;
  }, [rows, months, year, payeeFilter]);

  const total = useMemo(() => round2(filteredRows.reduce((a, r) => a + (Number(r.amount) || 0), 0)), [filteredRows]);
  const byPayee = useMemo(() => {
    const m = new Map();
    for (const r of filteredRows) m.set(r.payee, (m.get(r.payee) || 0) + (Number(r.amount) || 0));
    return Array.from(m.entries())
      .map(([name, amt]) => ({ name, amount: round2(amt) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredRows]);

  // Sortable table — click a header to sort, click again to flip.
  const [sortKey, setSortKey] = useState('pay_date');
  const [sortDir, setSortDir] = useState('desc');
  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const pick = (r) => {
      switch (sortKey) {
        case 'payee': return (r.payee || '').toLowerCase();
        case 'amount': return Number(r.amount) || 0;
        case 'covers': return r.period_start || '';
        case 'invoice_number': return (r.invoice_number || '').toLowerCase();
        case 'pay_date':
        default: return r.pay_date || '';
      }
    };
    return [...filteredRows].sort((a, b) => {
      const av = pick(a); const bv = pick(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filteredRows, sortKey, sortDir]);
  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'pay_date' ? 'desc' : 'asc'); }
  }
  const sortArrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // Contractor summary: one line per payee with the grand total for the
  // selection (by default the whole current year), number of payments and
  // the span of work covered. Collapsed until clicked; the column sort
  // applies inside each payee.
  const payeeGroups = useMemo(() => {
    const m = new Map();
    for (const r of sortedRows) {
      const key = r.payee || 'Unknown';
      if (!m.has(key)) m.set(key, { key, rows: [], total: 0, first: null, last: null });
      const g = m.get(key);
      g.rows.push(r);
      g.total = round2(g.total + (Number(r.amount) || 0));
      const ps = r.period_start || r.pay_date;
      const pe = r.period_end || r.pay_date;
      if (ps && (!g.first || ps < g.first)) g.first = ps;
      if (pe && (!g.last || pe > g.last)) g.last = pe;
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [sortedRows]);
  const [openPayees, setOpenPayees] = useState({}); // payee -> bool (default closed)
  const isPayeeOpen = (g) => !!openPayees[g.key];
  const togglePayee = (g) => setOpenPayees((p) => ({ ...p, [g.key]: !p[g.key] }));
  const allPayeesOpen = payeeGroups.length > 0 && payeeGroups.every(isPayeeOpen);
  const setAllPayees = (v) => setOpenPayees(Object.fromEntries(payeeGroups.map((g) => [g.key, v])));
  const shortDate = (iso) => (iso ? isoToDate(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

  // Month picker: selecting months sets the date range to their span
  // (so the data loads) and filters to exactly the chosen months.
  function addMonth(key) {
    if (!key || months.includes(key)) return;
    setYear('all');
    setMonths([...months, key].sort());
  }
  function removeMonth(key) { setMonths(months.filter((k) => k !== key)); }
  function clearMonths() { setMonths([]); }

  function downloadCsv() {
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [['Payee', 'Pay date', 'Covers from', 'Covers to', 'Amount (CAD)', 'Invoice #', 'Invoice date', 'Note'].join(',')];
    for (const r of sortedRows) lines.push([esc(r.payee), esc(r.pay_date), esc(r.period_start), esc(r.period_end), esc(r.amount), esc(r.invoice_number), esc(r.invoice_date), esc(r.note)].join(','));
    const csv = lines.join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `contractor-payments_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  const coversFmt = (iso) => isoToDate(iso).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const coversLabel = (r) => (r.period_start && r.period_end)
    ? `${coversFmt(r.period_start)} → ${coversFmt(r.period_end)}`
    : (r.period_start || r.period_end || '—');

  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
      <h2 style={{ marginBottom: 4 }}>Contractor Payments</h2>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginBottom: 'var(--space-3)' }}>
        Payouts you enter by hand — offshore developers, Jose, etc. (usually every two weeks). Admin only · amounts in CAD.
      </div>

      {error && <p className="error" style={{ color: 'var(--color-bad)' }}>{error}</p>}
      {info && <p style={{ color: 'var(--color-good, #0d9488)', fontSize: 13, margin: '0 0 8px' }}>{info}</p>}

      {/* Drop an invoice here to read it and auto-fill the form. */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropInvoice}
        onClick={() => fileRef.current && fileRef.current.click()}
        role="button"
        tabIndex={0}
        style={{
          border: '1.5px dashed var(--color-accent, #3b7cb5)', borderRadius: 12,
          background: 'rgba(59,124,181,.06)', color: 'var(--color-text, #0f172a)',
          padding: 16, textAlign: 'center', cursor: 'pointer', marginBottom: 'var(--space-3)', fontSize: 14,
        }}
      >
        {reading
          ? 'Reading invoice…'
          : invoiceFile
            ? `📎 ${invoiceFile.name} attached — fields filled below. Click to choose a different file.`
            : '📄 Drop an invoice here (PDF or photo), or click to choose — it reads it and fills in the fields below.'}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf,.pdf,.heic,.heif"
          style={{ display: 'none' }}
          onChange={(e) => handleInvoiceFile(e.target.files && e.target.files[0])}
        />
      </div>

      <form onSubmit={save} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
        <label className="form-field" style={{ flex: '1 1 160px' }}>
          <span>Payee</span>
          <input type="text" list="contractor-payees" value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="e.g. Jose" />
          <datalist id="contractor-payees">
            {knownPayees.map((p) => <option key={p} value={p} />)}
          </datalist>
        </label>
        <label className="form-field">
          <span>Amount ($)</span>
          <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="cell-mono" />
        </label>
        <label className="form-field">
          <span>Date paid</span>
          <input type="date" value={payDate} max={todayISO()} onChange={(e) => setPayDate(e.target.value)} />
        </label>
        <label className="form-field">
          <span>Covers from</span>
          <input type="date" value={coversFrom} max={coversTo || undefined} onChange={(e) => setCoversFrom(e.target.value)} />
        </label>
        <label className="form-field">
          <span>Covers to</span>
          <input type="date" value={coversTo} onChange={(e) => setCoversTo(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary btn-sm" onClick={fillTwoWeeks} title="Set the period to the two weeks ending on the pay date">2-week period</button>
        <label className="form-field">
          <span>Invoice #</span>
          <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. 2026-021" />
        </label>
        <label className="form-field">
          <span>Invoice date</span>
          <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
        </label>
        <label className="form-field" style={{ flex: '1 1 180px' }}>
          <span>Note (optional)</span>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. invoice # / milestone" />
        </label>
        <button type="submit" className="btn-primary" disabled={saving || reading}>
          {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add payment')}
        </button>
        {editingId && <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>Cancel</button>}
      </form>

      {editingId && existingInvoice && !invoiceFile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 'var(--space-3)', fontSize: 13 }}>
          <span style={{ color: 'var(--color-text-dim)' }}>This payment has an invoice attached.</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => openInvoice(editingId, setError)}>View invoice</button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-bad, #b91c1c)' }}>
            <input type="checkbox" checked={removeInvoice} onChange={(e) => setRemoveInvoice(e.target.checked)} />
            Remove it
          </label>
          <span style={{ color: 'var(--color-text-dim)' }}>Or drop a new file above to replace it.</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', margin: 'var(--space-3) 0 var(--space-2)' }}>
        <label className="form-field" style={{ flex: '0 0 auto' }}>
          <span>Covers year</span>
          <select value={year} onChange={(e) => { const v = e.target.value; setYear(v); if (v !== 'all') setMonths([]); }}>
            <option value="all">All years</option>
            {YEAR_OPTS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="form-field" style={{ flex: '0 0 auto' }}>
          <span>Covers month</span>
          <select value="" onChange={(e) => { addMonth(e.target.value); e.target.value = ''; }}>
            <option value="">Add a month…</option>
            {MONTH_OPTS.map((m) => (
              <option key={m.key} value={m.key} disabled={months.includes(m.key)}>{m.label}</option>
            ))}
          </select>
        </label>
        <label className="form-field" style={{ flex: '0 0 auto' }}>
          <span>Payee</span>
          <select value={payeeFilter} onChange={(e) => setPayeeFilter(e.target.value)}>
            <option value="all">All payees</option>
            {knownPayees.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="form-field">
          <span>Paid from</span>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} disabled={months.length > 0 || year !== 'all'} />
        </label>
        <label className="form-field">
          <span>To</span>
          <input type="date" value={to} max={todayISO()} onChange={(e) => setTo(e.target.value)} disabled={months.length > 0 || year !== 'all'} />
        </label>
        <button type="button" className="btn-secondary" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        <button type="button" className="btn-primary" onClick={downloadCsv} disabled={sortedRows.length === 0}>Download CSV</button>
        {payeeGroups.length > 0 && (
          <button type="button" className="btn-secondary" onClick={() => setAllPayees(!allPayeesOpen)}>
            {allPayeesOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      {months.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-2)', fontSize: 13 }}>
          <span style={{ color: 'var(--color-text-dim)' }}>Covers:</span>
          {months.map((k) => {
            const opt = MONTH_OPTS.find((o) => o.key === k);
            const label = opt ? opt.label : k;
            return (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(59,124,181,.1)', color: 'var(--color-accent, #3b7cb5)', borderRadius: 999, padding: '3px 10px' }}>
                {label}
                <button type="button" onClick={() => removeMonth(k)} aria-label={`Remove ${label}`} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            );
          })}
          <button type="button" className="btn-secondary btn-sm" onClick={clearMonths}>Clear</button>
        </div>
      )}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('pay_date')}>Pay date{sortArrow('pay_date')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('payee')}>Payee{sortArrow('payee')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('covers')}>Covers{sortArrow('covers')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('amount')}>Amount{sortArrow('amount')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('invoice_number')}>Invoice{sortArrow('invoice_number')}</th>
              <th>Note</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}>Loading…</td></tr>
            ) : sortedRows.length === 0 ? (
              <tr><td colSpan={7} className="cell-muted">No contractor payments for this selection.</td></tr>
            ) : (
              <>
                {payeeGroups.map((g) => (
                  <Fragment key={g.key}>
                    <tr style={weekRowStyle} onClick={() => togglePayee(g)} title={isPayeeOpen(g) ? 'Hide payments' : 'Show payments'}>
                      <td>
                        {isPayeeOpen(g) ? '\u25be' : '\u25b8'}{' '}
                        <span style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>{g.rows.length} payment{g.rows.length === 1 ? '' : 's'}</span>
                      </td>
                      <td>{g.key}</td>
                      <td style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>
                        {g.first ? `${shortDate(g.first)} \u2192 ${shortDate(g.last)}` : ''}
                      </td>
                      <td className="cell-mono">{money(g.total)}</td>
                      <td colSpan={3} />
                    </tr>
                {isPayeeOpen(g) && g.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-mono" style={{ paddingLeft: 28 }}>{r.pay_date}</td>
                    <td>{r.payee}</td>
                    <td>{coversLabel(r)}</td>
                    <td className="cell-mono">{money(r.amount)}</td>
                    <td>
                      {r.invoice_number ? <span>{r.invoice_number}</span> : null}
                      {r.has_invoice
                        ? <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: r.invoice_number ? 6 : 0 }} onClick={() => openInvoice(r.id, setError)}>View</button>
                        : (!r.invoice_number ? <span className="cell-muted">—</span> : null)}
                    </td>
                    <td>{r.note || ''}</td>
                    <td>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => startEdit(r)}>Edit</button>
                      <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: 6, color: 'var(--color-bad, #b91c1c)' }} onClick={() => del(r.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                  </Fragment>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>
                  <td colSpan={3}>Total ({months.length ? `covers ${months.map((k) => (MONTH_OPTS.find((o) => o.key === k) || {}).label || k).join(', ')}` : year !== 'all' ? `covers ${year}` : `paid ${from} → ${to}`})</td>
                  <td className="cell-mono">{money(total)}</td>
                  <td colSpan={3}></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {byPayee.length > 1 && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 13, color: 'var(--color-text-dim)' }}>
          {byPayee.map((p) => `${p.name}: ${money(p.amount)}`).join('   ·   ')}
        </div>
      )}
    </div>
  );
}

// Admin manager view: everyone's hours over a date range, totals
// per employee, and a CSV export for payroll.
// Manager detail tables: one summary line per employee, collapsed until
// clicked. `rows` keeps its current sort inside each employee.
function groupByEmployee(rows) {
  const m = new Map();
  for (const r of rows) {
    const k = r.user_name || 'Unknown';
    if (!m.has(k)) m.set(k, { name: k, rows: [] });
    m.get(k).rows.push(r);
  }
  return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
}
function useOpenSet() {
  const [open, setOpen] = useState({}); // key -> bool (default closed)
  return {
    isOpen: (k) => !!open[k],
    toggle: (k) => setOpen((p) => ({ ...p, [k]: !p[k] })),
    setAll: (keys, v) => setOpen(Object.fromEntries(keys.map((k) => [k, v]))),
  };
}

function ManagerView() {
  const [from, setFrom] = useState(() => daysAgoISO(13)); // default: last 14 days
  const [to, setTo] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expRows, setExpRows] = useState([]);
  const [detailEmp, setDetailEmp] = useState('all');
  const [detailType, setDetailType] = useState('all');
  const hourOpen = useOpenSet();
  const expOpen = useOpenSet();
  const [sortKey, setSortKey] = useState('work_date');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get('/api/time/all', { params: { from, to } });
      setRows(r.data.entries || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const loadExp = useCallback(async () => {
    try {
      const r = await axios.get('/api/time/expenses/all', { params: { from, to } });
      setExpRows(r.data.expenses || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, [from, to]);
  useEffect(() => { loadExp(); }, [loadExp]);

  const totalsByEmployee = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.user_name, (m.get(r.user_name) || 0) + (Number(r.hours) || 0));
    return Array.from(m.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  }, [rows]);

  const grandTotal = useMemo(
    () => round2(rows.reduce((a, r) => a + (Number(r.hours) || 0), 0)),
    [rows]
  );

  async function downloadCsv() {
    setError(null);
    try {
      const r = await axios.get('/api/time/export', { params: { from, to }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee-hours_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  const expTotalsByEmployee = useMemo(() => {
    const m = new Map();
    for (const r of expRows) m.set(r.user_name, (m.get(r.user_name) || 0) + (Number(r.amount) || 0));
    return Array.from(m.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  }, [expRows]);

  const expGrandTotal = useMemo(
    () => round2(expRows.reduce((a, r) => a + (Number(r.amount) || 0), 0)),
    [expRows]
  );

  // Combined per-employee totals (hours + expenses) for the summary table.
  const combinedTotals = useMemo(() => {
    const hMap = new Map(totalsByEmployee);
    const eMap = new Map(expTotalsByEmployee);
    const names = Array.from(new Set([...hMap.keys(), ...eMap.keys()])).sort((a, b) => a.localeCompare(b));
    return names.map((name) => ({ name, hours: round2(hMap.get(name) || 0), expenses: round2(eMap.get(name) || 0) }));
  }, [totalsByEmployee, expTotalsByEmployee]);

  // Hours — detail: employee list for the filter dropdown.
  const detailEmployees = useMemo(
    () => Array.from(new Set(rows.map((r) => r.user_name))).sort((a, b) => String(a).localeCompare(String(b))),
    [rows]
  );

  // Hours — detail: filtered (employee + type) and sorted (clickable headers).
  const detailRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const filtered = rows.filter((r) =>
      (detailEmp === 'all' || r.user_name === detailEmp) &&
      (detailType === 'all' || (r.entry_type || 'regular') === detailType)
    );
    const pick = (r) => (
      sortKey === 'user_name' ? (r.user_name || '')
        : sortKey === 'entry_type' ? (r.entry_type || 'regular')
          : (r.work_date || '')
    );
    return [...filtered].sort((a, b) => {
      if (sortKey === 'hours') return ((Number(a.hours) || 0) - (Number(b.hours) || 0)) * dir;
      return String(pick(a)).localeCompare(String(pick(b))) * dir;
    });
  }, [rows, detailEmp, detailType, sortKey, sortDir]);
  const hourGroups = useMemo(() => groupByEmployee(detailRows), [detailRows]);
  const expGroups = useMemo(() => groupByEmployee(expRows), [expRows]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'work_date' ? 'desc' : 'asc'); }
  }
  const sortArrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  async function downloadExpCsv() {
    setError(null);
    try {
      const r = await axios.get('/api/time/expenses/export', { params: { from, to }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee-expenses_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
      <h2 style={{ marginBottom: 4 }}>All Employees</h2>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginBottom: 'var(--space-3)' }}>
        Everyone's logged hours — pick a range and export for payroll.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <label className="form-field">
          <span>From</span>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="form-field">
          <span>To</span>
          <input type="date" value={to} max={todayISO()} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button type="button" className="btn-primary" onClick={downloadCsv} disabled={rows.length === 0}>
          Download CSV
        </button>
      </div>

      {error && <p className="error" style={{ color: 'var(--color-bad)' }}>{error}</p>}

      <h3 style={{ marginBottom: 'var(--space-2)' }}>Totals ({from} → {to})</h3>
      <div className="table-wrap" style={{ marginBottom: 'var(--space-4)' }}>
        <table className="data-table">
          <thead>
            <tr><th>Employee</th><th>Total hours</th><th>Total expenses</th></tr>
          </thead>
          <tbody>
            {combinedTotals.length === 0 ? (
              <tr><td colSpan={3} className="cell-muted">Nothing logged in this range.</td></tr>
            ) : (
              <>
                {combinedTotals.map((e) => (
                  <tr key={e.name}>
                    <td>{e.name}</td>
                    <td className="cell-mono">{e.hours}</td>
                    <td className="cell-mono">{money(e.expenses)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td>All employees</td>
                  <td className="cell-mono">{grandTotal}</td>
                  <td className="cell-mono">{money(expGrandTotal)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'var(--space-5)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Hours — detail</h3>
        <select value={detailEmp} onChange={(e) => setDetailEmp(e.target.value)}>
          <option value="all">All employees</option>
          {detailEmployees.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={detailType} onChange={(e) => setDetailType(e.target.value)}>
          <option value="all">All types</option>
          {HOUR_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
          {detailRows.length} row{detailRows.length === 1 ? '' : 's'}
        </span>
        {hourGroups.length > 0 && (
          <button type="button" className="btn-secondary btn-sm"
            onClick={() => hourOpen.setAll(hourGroups.map((g) => g.name), !hourGroups.every((g) => hourOpen.isOpen(g.name)))}>
            {hourGroups.every((g) => hourOpen.isOpen(g.name)) ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('work_date')}>Date{sortArrow('work_date')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('user_name')}>Employee{sortArrow('user_name')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('entry_type')}>Type{sortArrow('entry_type')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('hours')}>Hours{sortArrow('hours')}</th>
            </tr>
          </thead>
          <tbody>
            {detailRows.length === 0 ? (
              <tr><td colSpan={4} className="cell-muted">No entries.</td></tr>
            ) : (
              hourGroups.map((g) => {
                const gb = hourBreakdown(g.rows);
                const tot = gb.total;
                const days = new Set(g.rows.map((r) => r.work_date)).size;
                return (
                  <Fragment key={g.name}>
                    <tr style={weekRowStyle} onClick={() => hourOpen.toggle(g.name)} title={hourOpen.isOpen(g.name) ? 'Hide days' : 'Show days'}>
                      <td>{hourOpen.isOpen(g.name) ? '\u25be' : '\u25b8'} {days} day{days === 1 ? '' : 's'}</td>
                      <td>{g.name}</td>
                      <td style={{ fontWeight: 400, color: 'var(--color-text-dim)' }}>{breakdownText(gb)}</td>
                      <td className="cell-mono">{tot} h</td>
                    </tr>
                    {hourOpen.isOpen(g.name) && g.rows.map((r) => (
                      <tr key={r.id}>
                        <td className="cell-mono" style={{ paddingLeft: 28 }}>{r.work_date}</td>
                        <td style={{ color: 'var(--color-text-dim)' }}>{r.user_name}</td>
                        <td>{typeLabel(r.entry_type)}</td>
                        <td className="cell-mono">{r.hours}</td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'var(--space-5)', marginBottom: 'var(--space-2)' }}>
        <h3 style={{ margin: 0 }}>Expenses</h3>
        <button type="button" className="btn-primary btn-sm" onClick={downloadExpCsv} disabled={expRows.length === 0}>
          Download expenses CSV
        </button>
        {expGroups.length > 0 && (
          <button type="button" className="btn-secondary btn-sm"
            onClick={() => expOpen.setAll(expGroups.map((g) => g.name), !expGroups.every((g) => expOpen.isOpen(g.name)))}>
            {expGroups.every((g) => expOpen.isOpen(g.name)) ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>Date</th><th>Employee</th><th>Category</th><th>Amount</th><th>Description</th><th>Receipt</th></tr>
          </thead>
          <tbody>
            {expRows.length === 0 ? (
              <tr><td colSpan={6} className="cell-muted">No expenses.</td></tr>
            ) : (
              expGroups.map((g) => (
                <Fragment key={g.name}>
                  <tr style={weekRowStyle} onClick={() => expOpen.toggle(g.name)} title={expOpen.isOpen(g.name) ? 'Hide expenses' : 'Show expenses'}>
                    <td>{expOpen.isOpen(g.name) ? '\u25be' : '\u25b8'} {g.rows.length} expense{g.rows.length === 1 ? '' : 's'}</td>
                    <td>{g.name}</td>
                    <td />
                    <td className="cell-mono">{money(g.rows.reduce((a, r) => a + (Number(r.amount) || 0), 0))}</td>
                    <td colSpan={2} style={{ fontWeight: 400, fontSize: 12, color: 'var(--color-text-dim)' }}>
                      {g.rows.filter((r) => r.has_receipt).length} of {g.rows.length} with receipts
                    </td>
                  </tr>
              {expOpen.isOpen(g.name) && g.rows.map((r) => (
                <tr key={r.id}>
                  <td className="cell-mono" style={{ paddingLeft: 28 }}>{r.expense_date}</td>
                  <td style={{ color: 'var(--color-text-dim)' }}>{r.user_name}</td>
                  <td>{r.category}</td>
                  <td className="cell-mono">{money(r.amount)}</td>
                  <td>{r.description || ''}</td>
                  <td>
                    {r.has_receipt
                      ? <button type="button" className="btn-secondary btn-sm" onClick={() => openReceipt(r.id, setError)}>View</button>
                      : <span className="cell-muted">—</span>}
                  </td>
                </tr>
              ))}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
